// src/plugins/sales-forecast/lib/useSigmaElementData.ts
import { useConfig, useElementData, useElementColumns } from "@sigmacomputing/plugin";
// import { data } from "react-router-dom";

/** The typed rows your app/API expects */
export type EhRow = {
  address: string | null;
  city: string | null;
  neighborhood: string | null;
  houseType: string | null;
  contractDate: string | null; // ISO string
  officeGreenLight: string | null;
  activeComps: number | null;
  market: string | null;
  schoolDistrict: string | null;
  floorPlan: string | null;
  style: string | null;
  price: number | null;
  sqft: number | null;
};

/** Convenience aliases */
type ColId = string;
type DisplayName = string;
type ColumnMeta = { name?: string };
type RawCols = Record<ColId, ColumnMeta>;
type ColumnArrays = Record<ColId, any[]>;

/* ------------------------- helpers ------------------------- */

function norm(s?: string) {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}
function softNorm(s?: string) {
  return (s ?? "").toLowerCase().replace(/[\s_\-./]+/g, "").trim();
}

/** Find a column id in rawCols by (case-insensitive) display name */
function findColIdByDisplayName(rawCols: RawCols, ...candidates: DisplayName[]): ColId | null {
  const wanted = candidates.map(norm);
  for (const [id, meta] of Object.entries(rawCols)) {
    if (wanted.includes(norm(meta?.name))) return id;
  }
  return null;
}

/** Translate a column id to its human display name */
function idToName(rawCols: RawCols, id: ColId | null): DisplayName {
  return id ? (rawCols[id]?.name ?? "(unknown)") : "(unresolved)";
}

/** Convert whatever shape `useElementData` returns into { colId: value[] } */
function asColumnArrays(data: any): ColumnArrays | null {
  if (!data) return null;

  // Common Sigma shape: object of arrays keyed by columnId
  if (typeof data === "object" && !Array.isArray(data)) {
    const keys = Object.keys(data);
    if (keys.every((k) => Array.isArray((data as any)[k]))) {
      return data as ColumnArrays;
    }
  }

  // Defensive: array of row objects (we'll transpose)
  if (Array.isArray(data) && data.length && typeof data[0] === "object") {
    const cols: ColumnArrays = {};
    for (const row of data as any[]) {
      for (const [k, v] of Object.entries(row)) {
        (cols[k] ??= []).push(v);
      }
    }
    return cols;
  }

  return null;
}

/**
 * Resolve the *data* column ID actually present in the element's output for a desired field.
 * Tries, in order:
 *   1) Use the intended column ID if it exists in presentDataIds.
 *   2) Exact display-name match (case-insensitive).
 *   3) Soft display-name match (punctuation/whitespace stripped).
 */
function resolveDataIdForField(opts: {
  intendedId: ColId | null;
  intendedName: DisplayName; // from rawCols
  presentDataIds: ColId[];
  rawCols: RawCols;
}): { dataId: ColId | null; via: "id" | "name" | "soft-name" | "none" } {
  const { intendedId, intendedName, presentDataIds, rawCols } = opts;

  // 1) Exact id hit
  if (intendedId && presentDataIds.includes(intendedId)) {
    return { dataId: intendedId, via: "id" };
  }

  // Build name maps once
  const nameToDataIdExact: Record<string, ColId> = {};
  const softNameToDataId: Record<string, ColId> = {};
  for (const id of presentDataIds) {
    const nm = rawCols[id]?.name;
    if (!nm) continue;
    const n = norm(nm);
    const sn = softNorm(nm);
    if (!(n in nameToDataIdExact)) nameToDataIdExact[n] = id;
    if (!(sn in softNameToDataId)) softNameToDataId[sn] = id;
  }

  // 2) Exact name
  const intendedNorm = norm(intendedName);
  if (intendedNorm && nameToDataIdExact[intendedNorm]) {
    return { dataId: nameToDataIdExact[intendedNorm], via: "name" };
  }

  // 3) Soft name
  const intendedSoft = softNorm(intendedName);
  if (intendedSoft && softNameToDataId[intendedSoft]) {
    return { dataId: softNameToDataId[intendedSoft], via: "soft-name" };
  }

  return { dataId: null, via: "none" };
}

/* --------------------------- hook -------------------------- */

/**
 * Reads rows from the bound Sigma element and maps to EhRow objects.
 * Works with editor-panel picks (preferred) OR display-name fallbacks.
 * Hooks are called unconditionally; function ALWAYS returns an array.
 */
export function useSigmaElementData(): EhRow[] {
  const cfg = useConfig() as any;

  // Prefer editor-panel key "ehMaster"; fall back to legacy "dataSource"
  const ehMasterMaybe: string | undefined = (cfg?.ehMaster ?? cfg?.dataSource) || undefined;

  // Hooks must be called with string → coerce undefined → ""
  const elementId: string = ehMasterMaybe ?? "";

  // Pull every render; guard after
  const rawDataAny = useElementData('ehMaster');
  // function printOnce(label: string, value: any) {
  //   if ((window as any).__sf_once__?.has(label)) return;
  //   (window as any).__sf_once__ = (window as any).__sf_once__ || new Set();
  //   (window as any).__sf_once__.add(label);
  //   // eslint-disable-next-line no-console
  //   console.log(label, value);
  // }
  
  // after: const rawDataAny = useElementData(elementId);
  // printOnce("[SF][hook] elementId", elementId);
  // printOnce("[SF][hook] typeof rawDataAny", typeof rawDataAny);
  // printOnce("[SF][hook] rawDataAny (first 1k chars)", (() => {
  //   try { return JSON.stringify(rawDataAny).slice(0, 1000); } catch { return rawDataAny; }
  // })());
  // right after: const rawDataAny = useElementData(elementId);
  // console.log("ONE OFF: [SF][hook] rawDataAny typeof/keys:", typeof rawDataAny, rawDataAny && Object.keys(rawDataAny));  
  const rawCols = useElementColumns(elementId);
  // console.log("FIRST RAW COLS: ", rawCols)
  // Not bound or not streamed yet
  if (!ehMasterMaybe || !rawDataAny || !rawCols) return [];

  // Normalize data to object-of-arrays
  const colArrays = asColumnArrays(rawDataAny);
  if (!colArrays) return [];

  // 1) Resolve intended column IDs (from picks or by fallback name)
  const intendedIds: Record<keyof EhRow, ColId | null> = {
    address:          cfg?.addressCol           ?? findColIdByDisplayName(rawCols, "Address"),
    city:             cfg?.cityCol              ?? findColIdByDisplayName(rawCols, "City"),
    neighborhood:     cfg?.neighborhoodCol      ?? findColIdByDisplayName(rawCols, "Neighborhood - Grouped", "Neighborhood Grouped"),
    houseType:        cfg?.houseTypeCol         ?? findColIdByDisplayName(rawCols, "House Type"),
    contractDate:     cfg?.contractDateCol      ?? findColIdByDisplayName(rawCols, "Contract Date"),
    officeGreenLight: cfg?.officeGreenLightCol  ?? findColIdByDisplayName(rawCols, "Office Green Light"),
    activeComps:      cfg?.activeCompsCol       ?? findColIdByDisplayName(rawCols, "Active Comps"),
    market:           cfg?.marketCol            ?? findColIdByDisplayName(rawCols, "Market"),
    schoolDistrict:   cfg?.schoolDistrictCol    ?? findColIdByDisplayName(rawCols, "School District"),
    floorPlan:        cfg?.floorPlanCol         ?? findColIdByDisplayName(rawCols, "Floor Plan"),
    style:            cfg?.styleCol             ?? findColIdByDisplayName(rawCols, "Style"),
    price:            cfg?.priceCol             ?? findColIdByDisplayName(rawCols, "Price"),
    sqft:             cfg?.sqftCol              ?? findColIdByDisplayName(rawCols, "Square Feet")
  } as any;

  // 2) Translate intended IDs to display names
  const intendedNames: Record<keyof EhRow, DisplayName> = {
    address:          idToName(rawCols, intendedIds.address),
    city:             idToName(rawCols, intendedIds.city),
    neighborhood:     idToName(rawCols, intendedIds.neighborhood),
    houseType:        idToName(rawCols, intendedIds.houseType),
    contractDate:     idToName(rawCols, intendedIds.contractDate),
    officeGreenLight: idToName(rawCols, intendedIds.officeGreenLight),
    activeComps:      idToName(rawCols, intendedIds.activeComps),
    market:           idToName(rawCols, intendedIds.market),
    schoolDistrict:   idToName(rawCols, intendedIds.schoolDistrict),
    floorPlan:        idToName(rawCols, intendedIds.floorPlan),
    style:            idToName(rawCols, intendedIds.style),
    price:            idToName(rawCols, intendedIds.price),
    sqft:             idToName(rawCols, intendedIds.sqft)
  } as any;

  // Present data IDs are the keys of the column arrays
  const presentDataIds = Object.keys(colArrays);

  // 3) Resolve each field → actual data ID currently present
  const resolved = {
    address:          resolveDataIdForField({ intendedId: intendedIds.address,          intendedName: intendedNames.address,          presentDataIds, rawCols }),
    city:             resolveDataIdForField({ intendedId: intendedIds.city,             intendedName: intendedNames.city,             presentDataIds, rawCols }),
    neighborhood:     resolveDataIdForField({ intendedId: intendedIds.neighborhood,     intendedName: intendedNames.neighborhood,     presentDataIds, rawCols }),
    houseType:        resolveDataIdForField({ intendedId: intendedIds.houseType,        intendedName: intendedNames.houseType,        presentDataIds, rawCols }),
    contractDate:     resolveDataIdForField({ intendedId: intendedIds.contractDate,     intendedName: intendedNames.contractDate,     presentDataIds, rawCols }),
    officeGreenLight: resolveDataIdForField({ intendedId: intendedIds.officeGreenLight, intendedName: intendedNames.officeGreenLight, presentDataIds, rawCols }),
    activeComps:      resolveDataIdForField({ intendedId: intendedIds.activeComps,      intendedName: intendedNames.activeComps,      presentDataIds, rawCols }),
    market:           resolveDataIdForField({ intendedId: intendedIds.market,           intendedName: intendedNames.market,           presentDataIds, rawCols }),
    schoolDistrict:   resolveDataIdForField({ intendedId: intendedIds.schoolDistrict,   intendedName: intendedNames.schoolDistrict,   presentDataIds, rawCols }),
    floorPlan:        resolveDataIdForField({ intendedId: intendedIds.floorPlan,        intendedName: intendedNames.floorPlan,        presentDataIds, rawCols }),
    style:            resolveDataIdForField({ intendedId: intendedIds.style,            intendedName: intendedNames.style,            presentDataIds, rawCols }),
    price:            resolveDataIdForField({ intendedId: intendedIds.price,            intendedName: intendedNames.price,            presentDataIds, rawCols }),
    sqft:             resolveDataIdForField({ intendedId: intendedIds.sqft,             intendedName: intendedNames.sqft,             presentDataIds, rawCols })
  };

  const dataIdsForFields: Record<keyof EhRow, ColId | null> = {
    address:          resolved.address.dataId,
    city:             resolved.city.dataId,
    neighborhood:     resolved.neighborhood.dataId,
    houseType:        resolved.houseType.dataId,
    contractDate:     resolved.contractDate.dataId,
    officeGreenLight: resolved.officeGreenLight.dataId,
    activeComps:      resolved.activeComps.dataId,
    market:           resolved.market.dataId,
    schoolDistrict:   resolved.schoolDistrict.dataId,
    floorPlan:        resolved.floorPlan.dataId,
    style:            resolved.style.dataId,
    price:            resolved.price.dataId,
    sqft:             resolved.sqft.dataId
  } as any;

  /* ------------- diagnostics (comment out later) ------------- */
  // eslint-disable-next-line no-console
  // console.log("START: [SF][hook] intended IDs:", intendedIds);
  // console.log("Raw Data Any:", rawDataAny);
  // eslint-disable-next-line no-console
  // console.log("[SF][hook] intended names:", intendedNames);
  // eslint-disable-next-line no-console
  // console.log("[SF][hook] rawData keys (present now):", presentDataIds.slice(0, 12), presentDataIds.length > 12 ? "...+" + (presentDataIds.length - 12) : "");
  // eslint-disable-next-line no-console
  // console.log("FINISHED: [SF][hook] resolved data IDs for fields:", dataIdsForFields, {
  //   vias: {
  //     address: resolved.address.via,
  //     city: resolved.city.via,
  //     neighborhood: resolved.neighborhood.via,
  //     houseType: resolved.houseType.via,
  //     contractDate: resolved.contractDate.via,
  //     activeComps: "mOO8soBJDy",
  //   },
  // });
  /* ----------------------------------------------------------- */

  // 4) Determine row count from any resolved data ID; else first data id; else []
  let idForLength: ColId | null =
    dataIdsForFields.address ??
    dataIdsForFields.city ??
    dataIdsForFields.neighborhood ??
    dataIdsForFields.houseType ??
    dataIdsForFields.contractDate ??
    dataIdsForFields.officeGreenLight ??
    dataIdsForFields.activeComps ??
    dataIdsForFields.market ??
    dataIdsForFields.schoolDistrict ??
    dataIdsForFields.floorPlan ??
    dataIdsForFields.style ??
    dataIdsForFields.price ??
    dataIdsForFields.sqft ??
    null;

  if (!idForLength) idForLength = presentDataIds[0] ?? null;
  if (!idForLength) return [];

  const sourceArray = colArrays[idForLength];
  if (!Array.isArray(sourceArray) || sourceArray.length === 0) return [];

  const len = sourceArray.length;

  const pick = (dataId: ColId | null, i: number) =>
    dataId && Array.isArray(colArrays[dataId]) ? colArrays[dataId][i] : null;

  // 5) Build typed rows
  const rows: EhRow[] = new Array(len);
  for (let i = 0; i < len; i++) {
    const dt  = pick(dataIdsForFields.contractDate, i);
    const dt2 = pick(dataIdsForFields.officeGreenLight, i);
    rows[i] = {
      address:          pick(dataIdsForFields.address, i),
      city:             pick(dataIdsForFields.city, i),
      neighborhood:     pick(dataIdsForFields.neighborhood, i),
      houseType:        pick(dataIdsForFields.houseType, i),
      contractDate:     dt ? new Date(dt).toISOString() : null,
      officeGreenLight: dt2 ? new Date(dt2).toISOString() : null,
      activeComps:      pick(dataIdsForFields.activeComps, i) !== null
        ? Number(pick(dataIdsForFields.activeComps, i))
        : null,
      market:           pick(dataIdsForFields.market, i),
      schoolDistrict:   pick(dataIdsForFields.schoolDistrict, i),
      floorPlan:        pick(dataIdsForFields.floorPlan, i),
      style:            pick(dataIdsForFields.style, i),
      price:            pick(dataIdsForFields.price, i) !== null
        ? Number(pick(dataIdsForFields.price, i))
        : null,
      sqft:             pick(dataIdsForFields.sqft, i) !== null
        ? Number(pick(dataIdsForFields.sqft, i))
        : null,
    };
  }

  return rows;
}